import { executeRequest } from '@/core/either';
import { http } from '@/core/api';
import { EXCLUDE_STABLECOINS_REGEX } from '@/shared/common/constants/stablecoinCountry.constant';
import { filterTruthyParams } from '@/shared/common/utils/requestParams.util';
import type { ApiResult } from '@/core/either';

// Types
import type { IDestinatary, IDestinataryP2P, IWalletSearchContact } from '@/types/InterfaceDestinatary';
import type {
  ContactParams,
  DestinataryResponseDetail,
  ValidateBankRegulatorParams,
  Destinatary as DestinataryType,
  ParamsDynamicInputs,
  TypeContact,
} from '@/types/DestinataryType';
import type { IDynamicInput } from '@/types/IDynamicInput';
import type { P2pContactCreatePayload } from '@/shared/common/types/p2pContact.type';
import type {
  ContactComplianceValidationParams,
  SaveDestinataryParams,
} from '@/shared/common/types/destinatary.type';

export const destinataryService = {
  /**
   * Obtiene el listado de contactos según el tipo y parámetros de búsqueda proporcionados.
   *
   * @param type - Tipo de contacto a obtener ('ALL' | 'P2P' | 'REMITTANCE').
   * @param params - Parámetros opcionales para filtrar los contactos.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getContacts(
    type: TypeContact = 'ALL',
    params?: ContactParams
  ): Promise<ApiResult<IDestinatary[]>> {
    return executeRequest(() =>
      http.get<IDestinatary[]>(`beneficiary/b2b/contacts/${type}`, {
        params: {
          countryCode: EXCLUDE_STABLECOINS_REGEX,
          ...filterTruthyParams(params),
          origin: 'B2B',
        }
      })
    );
  },

  /**
   * Obtiene el detalle de un contacto específico.
   * 
   * @param beneficiaryId - ID del beneficiario.
   * @param params - Parámetros opcionales (branch, paymentType).
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getDestinatary(
    beneficiaryId: string,
    params?: { branch?: string; paymentType?: string }
  ): Promise<ApiResult<DestinataryResponseDetail>> {
    return executeRequest(() =>
      http.get<DestinataryResponseDetail>(`beneficiary/b2b/${beneficiaryId}`, { params })
    );
  },

  /**
   * Valida si un beneficiario cumple con las regulaciones bancarias (CCA).
   * 
   * @param params - Parámetros de validación (beneficiaryId, bankingRegulator, amount, movementType).
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  validateBeneficiaryBankingRegulator({
    beneficiaryId,
    ...restParams
  }: ValidateBankRegulatorParams): Promise<ApiResult<DestinataryType>> {
    return executeRequest(() =>
      http.get<DestinataryType>(
        `beneficiary/b2b/banking-regulators/valid/limit/${beneficiaryId}`,
        { 
          params: restParams, 
          headers: { 'X-API-VERSION': '2' }
        }
      )
    );
  },

  /**
   * Crea un contacto Cuenta Global (P2P) a partir de la identidad Global66 resuelta
   * en el lookup de la llave Bre-B.
   *
   * @param payload - Identificadores del cliente Global66 a agregar como contacto.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  createP2pContact(payload: P2pContactCreatePayload): Promise<ApiResult<void>> {
    return executeRequest(() => http.post<void>('beneficiary/b2b/p2p', payload));
  },

  /**
   * Obtiene el listado de contactos Cuenta Global (P2P) del cliente en una moneda dada.
   *
   * @param currencyCode - Moneda por la que se filtran los contactos.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getP2pContacts(currencyCode: string): Promise<ApiResult<IDestinataryP2P[]>> {
    return executeRequest(() =>
      http.get<IDestinataryP2P[]>(`beneficiary/b2b/contacts/p2p/${currencyCode}`)
    );
  },

  /**
   * Obtiene la configuración de inputs dinámicos del formulario de destinatario
   * para un país/tipo de pago dados.
   *
   * @param params - País, tipo de pago y branch a consultar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getDynamicInputs(params: ParamsDynamicInputs): Promise<ApiResult<IDynamicInput[]>> {
    return executeRequest(() =>
      http.get<IDynamicInput[]>('beneficiary/b2b/config/form', { params })
    );
  },

  /**
   * Crea un destinatario. `authValue`/`authType` viajan como headers de verificación OTP,
   * no como parte del body.
   *
   * @param params - Datos del destinatario y credenciales de autorización.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  createDestinatary({
    destinataryData,
    authValue,
    authType,
  }: SaveDestinataryParams): Promise<ApiResult<DestinataryType>> {
    return executeRequest(() =>
      http.post<DestinataryType>('beneficiary/b2b', destinataryData, {
        headers: { authValue, authType },
      })
    );
  },

  /**
   * Actualiza un destinatario existente. `authValue`/`authType` viajan como headers de
   * verificación OTP, no como parte del body.
   *
   * @param params - Datos del destinatario y credenciales de autorización.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  updateDestinatary({
    destinataryData,
    authValue,
    authType,
  }: SaveDestinataryParams): Promise<ApiResult<DestinataryType>> {
    return executeRequest(() =>
      http.put<DestinataryType>('beneficiary/b2b', destinataryData, {
        headers: { authValue, authType },
      })
    );
  },

  /**
   * Valida el formulario de destinatario contra las reglas del backend, sin crearlo.
   *
   * @param destinataryData - Datos del destinatario a validar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  validateDestinataryForm(destinataryData: DestinataryType): Promise<ApiResult<DestinataryType>> {
    return executeRequest(() =>
      http.post<DestinataryType>('beneficiary/b2b/validate-form', destinataryData)
    );
  },

  /**
   * Busca un contacto Cuenta Global (P2P) por email o username.
   *
   * @param value - Email o username a buscar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getContactSearch(value: string): Promise<ApiResult<IWalletSearchContact>> {
    return executeRequest(() =>
      http.get<IWalletSearchContact>('beneficiary/b2b/p2p/search', { params: { value } })
    );
  },

  /**
   * Valida el cumplimiento (KYC/compliance) de un contacto antes de habilitarlo
   * como destinatario. Requiere `X-API-VERSION: 2`.
   *
   * @param params - Id del beneficiario y si es una cuenta B2B.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getContactComplianceValidation({
    beneficiaryId,
    isB2b,
  }: ContactComplianceValidationParams): Promise<ApiResult<unknown>> {
    return executeRequest(() =>
      http.get<unknown>(`beneficiary/b2b/compliance/validate/${beneficiaryId}`, {
        params: { isB2b },
        headers: { 'X-API-VERSION': '2' },
      })
    );
  },
};

export default destinataryService;
